<template>
    <v-card :loading="loading" :disabled="loading" flat>
        <v-row v-if="!pickWave">
            <v-col cols=12>
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </v-col>
        </v-row>
        <v-container fluid v-else>
            <v-row>
                <v-col cols="12">
                    <v-sheet elevation="1" class="pa-4">
                        <v-row>
                            <v-col cols="1">
                                #{{pickWave.id}}
                            </v-col>
                            <v-col cols="2">
                                Status: {{ pickWave.status }}
                            </v-col>
                            <v-col cols="2">
                                Picker: {{pickWave.picker.displayName}}
                            </v-col>
                            <v-col cols="2">
                                Start Date: {{pickWave.start_date}}
                            </v-col>
                            <v-col cols="2">
                                End Date: {{pickWave.end_date}}
                            </v-col>
                            <v-col cols="3">
                                Orders: <v-chip v-for="id in pickWave.order_ids" :key="`_order_id_${id}`" small class="mr-2">{{ id }}</v-chip>
                            </v-col>
                        </v-row>
                    </v-sheet>
                </v-col>
            </v-row>
            <v-row v-if="pickWave.status=='start'" >
                <v-col cols="12">
                    <v-card>
                        <v-card-title>Pickwave detail</v-card-title>
                        <v-simple-table>
                            <slot>
                                <thead>
                                    <tr>
                                        <td>Product</td>
                                        <td>Location</td>
                                        <td>Needed for orders</td>
                                        <td>Already picked</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, idx) in pickingLocations" :key="item.location.id">
                                        <td>{{ item.product.name }}</td>
                                        <td>{{ item.location.location }}</td>
                                        <td>{{ item.quantity }}</td>
                                        <td><v-chip :color="getCardColor(item)">{{ item.picked }}</v-chip></td>
                                        <td>
                                            <v-btn @click="resetPicking(idx)" :disabled="item.picked==0" small class="mr-2">Reset</v-btn>
                                            <v-btn @click="pickItem({code: item.product.code, quantity: -1, location: item.location.id})" :disabled="item.picked==0" small class="mr-2">Remove 1 item</v-btn>
                                            <v-btn @click="pickItem({code: item.product.code, quantity: 1, location: item.location.id})" :disabled="item.picked>=item.quantity" small class="mr-2">Pick 1 item</v-btn>
                                            <v-btn color="primary" @click="pickItem({code: item.product.code, quantity: item.quantity - item.picked, location: item.location.id})" :disabled="item.picked >= item.quantity" small>Pick All</v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </slot>
                        </v-simple-table>
                    </v-card>
                </v-col>
            </v-row>
            <v-row v-if="pickWave.status=='start'">
                <v-col cols="12" class="d-flex justify-center">
                    <v-btn @click="saveAndCompletePickWave">Apply picking and dispatch items in pending boxes</v-btn>
                </v-col>
            </v-row>
            <v-row v-if="pickWave.status=='finished'">
                <v-col>
                    <v-card>
                        <v-card-title>Pickwave detail</v-card-title>
                        <v-simple-table>
                            <slot>
                                <thead>
                                    <tr>
                                        <td>Product</td>
                                        <td>Picked</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in pickWave.items" :key="`_pickwave_item_${item.id}`">
                                        <td>{{ item.product.name }}</td>
                                        <td>{{ item.picked }}</td>
                                    </tr>
                                </tbody>
                            </slot>
                        </v-simple-table>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
        <v-dialog v-model="pendingBoxDialog" persistent>
            <v-card>
                <v-card-title>Dispatch to pending boxes</v-card-title>
                <v-row>
                    <v-col>
                        <v-card v-for="(pendingBox, idx) in pendingBoxes" :key="'pendingbox_'+idx">
                            <v-card-title>Product to dispatch in pending box "{{pendingBox.name}}"</v-card-title>
                            <v-card-text>
                                <v-simple-table>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">
                                                Product Name
                                            </th>
                                            <th class="text-left">
                                                SKU
                                            </th>
                                            <th class="text-left">
                                                Code
                                            </th>
                                            <th class="text-left">
                                                Quantity
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="row in pendingBox.items"
                                            :key="row.id"
                                        >
                                            <td>{{ row.product.name }}</td>
                                            <td>{{ row.product.sku }}</td>
                                            <td>{{ row.product.code }}</td>
                                            <td>{{ row.quantity }}</td>
                                        </tr>
                                    </tbody>
                                </template>
                                </v-simple-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" :href="`${REACT_URL()}/build/box`">Ok it's correctly dispatched</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {REACT_URL} from "@/common/config-local";
export default {
    data: () => ({
        pickwaveId: null,
        inputs:[],
        lastScannedCode: '',
        scannerTimer : null,
        pendingBoxes:null,
        pendingBoxDialog:null
    }),
    computed:{
        ...mapGetters("pickwave",["pickWave", "loading","pickingLocations"]),
        pickwaveStatus(){
            let quantity = 0;
            let picked = 0;
            for (let index = 0; index < this.pickingLocations.length; index++) {
                const item = this.pickingLocations[index];
                quantity += item.quantity;
                picked += item.picked;
            }
            if(quantity == picked){
                return "complete";
            }
            else if(picked > 0 && picked < quantity){
                return "partially";
            }
            else{
                return "incomplete";
            }
        }
    },
    mounted(){
        this.initialize();
        window.addEventListener("keypress", function(e) {
            this.onGlobalKeyPressed(e)
        }.bind(this));
    },
    methods:{
      REACT_URL() {
        return REACT_URL
      },
        ...mapActions("pickwave", ["getPickWave", "getPickingLocations", "pickItem","resetPicking", "commitPickup", "completePickWave"]),
        initialize() {
            if (this.$route.params.id) {
                this.pickwaveId = this.$route.params.id;
                this.getPickWave(this.pickwaveId).then(() => {
                    this.getPickingLocations(this.pickwaveId)
                });
            } else {
                this.$route.push("/pickwave");
            }
        },
        saveAndCompletePickWave(){
            this.commitPickup(this.pickwaveId)
            .then(() => {
                this.completePickWave(this.pickwaveId)
                .then(({pendingBoxes}) => {
                    this.pendingBoxes = pendingBoxes;
                    this.pendingBoxDialog = true;
                })
            })
        },
        onGlobalKeyPressed(e){
            let charCode = (typeof e.which == "number") ? e.which : e.keyCode;
            clearTimeout(this.scannerTimer);
            if(charCode != 13){
                this.lastScannedCode += String.fromCharCode(charCode);
                this.scannerTimer = setTimeout(() => {
                    this.lastScannedCode = '';
                }, 200);
            }else{
                let lastCode = this.lastScannedCode;
                this.lastScannedCode = '';
                if(lastCode){
                    this.pickItem({code:lastCode, quantity:1});
                }
            }
        },
        getCardColor(item){
            if(!item.picked){
                return 'red'
            } else if(item.picked < item.quantity){
                return 'orange'
            } else if(item.picked > item.quantity) {
                return 'red'
            }else{
                return 'green'
            }
        }
    }
}
</script>

<style>

</style>